import React, { useState } from 'react';
import { assignIn, get, isEmpty, isFunction, isNil, noop } from 'lodash';
import { InputAdornment } from '@material-ui/core';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import TextField from '@material-ui/core/TextField';
import {
    DEFAULT_INPUT_PROPS,
    DEFAULT_REQUIRED_INPUT_PROPS,
    GENERIC_REQUIRED_MESSAGE,
    MATERIAL_UI_SELECT_PROPS,
    TEXT_FIELD_TYPE_PASSWORD,
    TEXT_FIELD_TYPE_TEXT,
} from '../../utilities/FormUtilities';
import NgpField from './NgpField';
import NgpFieldHelpTip from './NgpFieldHelpTip';
import NgpPasswordVisibility from './NgpPasswordVisibility';

/** @typedef {import('react-hook-form').UseControllerOptions['rules']} FieldRules */
/** @typedef {import('@material-ui/core').TextFieldProps} TextFieldProps */

/**
 * @typedef NgpTextFieldProps
 * @property {string} fieldName
 * @property {string} label
 * @property {string} className
 * @property {string} helpTipText
 * @property {boolean} disabled
 * @property {boolean} required
 * @property {boolean} trimRequired Trims field values (except password fields) for text fields when `required` = `true`. Defaults to `true`.
 * @property {boolean} multiline
 * @property {boolean} togglePasswordVisibility
 * @property {boolean} select
 * @property {TextFieldProps['SelectProps']} selectProps
 * @property {TextFieldProps['inputProps']} inputProps
 * @property {TextFieldProps['InputProps']} InputProps
 * @property {TextFieldProps['inputRef']} inputRef
 * @property {TextFieldProps['onClick']} onClick
 * @property {TextFieldProps['onChange']} onChangeFunc
 * @property {TextFieldProps['onBlur']} onBlurFunc
 * @property {TextFieldProps['color']} color
 * @property {FieldRules} fieldRules
 * @property {TEXT_FIELD_TYPE_PASSWORD | TEXT_FIELD_TYPE_TEXT} fieldType
 * @property {boolean} showCharCounter When `true` and `inputProps.maxLength` is set, renders an portal/ui-style "X / max" counter aligned to the bottom-right of the field.
 */

/**
 * @param {React.PropsWithChildren<Partial<NgpTextFieldProps>>} props
 * @returns
 */
function NgpTextFieldCharCounter({ control, fieldName, maxLength }) {
    const watchedValue = useWatch({ control, name: fieldName });
    return `${String(watchedValue || '').length} / ${maxLength}`;
}

export default function NgpTextField(props) {
    const {
        fieldName,
        label,
        required = false,
        disabled,
        inputProps,
        InputProps: customInputProps,
        inputRef,
        onBlurFunc = noop,
        onChangeFunc = noop,
        multiline = false,
        fieldType,
        children,
        className,
        helpTipText,
        onClick = noop,
        togglePasswordVisibility = false,
        select = false,
        selectProps,
        inputLabelProps,
        trimRequired = true,
        fieldRules,
        color,
        fullWidth = false,
        showCharCounter = false,
    } = props;

    const { control, errors } = useFormContext();
    const maxLength = inputProps && inputProps.maxLength;
    const counterEnabled = showCharCounter && typeof maxLength === 'number';
    const [theFieldType, setTheFieldType] = useState(fieldType || TEXT_FIELD_TYPE_TEXT);

    /** @type {FieldRules} */
    const rules = assignIn({}, fieldRules);

    if (required === true) {
        rules.required = GENERIC_REQUIRED_MESSAGE;
    }

    if (theFieldType === 'text') {
        const origValidate = rules.validate;
        let origValidateRules = {};

        if (isFunction(origValidate)) {
            origValidateRules.default = origValidate;
        } else {
            origValidateRules = origValidate;
        }

        rules.validate = {
            ...origValidateRules,
            whitespace: (value) => {
                const strValue = String(value || '');

                if (required === true && trimRequired === true && isEmpty(strValue.trim())) {
                    return GENERIC_REQUIRED_MESSAGE;
                }

                return true;
            },
        };
    }

    const defaultInputProps = required ? DEFAULT_REQUIRED_INPUT_PROPS : DEFAULT_INPUT_PROPS;

    // Strip defaultValue from inputProps before it reaches the native <input>.
    // DEFAULT_INPUT_PROPS includes defaultValue for RHF Controller registration,
    // but when callers pass the same object as inputProps, MUI spreads it onto
    // the native <input>, causing a "both value and defaultValue" warning.
    // Fix console error for Warning:
    // ForwardRef(InputBase) contains an input of type text with both value and defaultValue props.
    // Input elements must be either controlled or uncontrolled (specify either the value prop,
    // or the defaultValue prop, but not both).
    // Decide between using a controlled or uncontrolled input element and remove one of these props
    const sanitizedInputProps = (() => {
        if (!inputProps) return undefined;
        const { defaultValue: _ignored, ...rest } = inputProps;
        return rest;
    })();

    const theSelectProps = select ? assignIn({}, MATERIAL_UI_SELECT_PROPS, selectProps || {}) : {};
    const theOtherInputProps =
        TEXT_FIELD_TYPE_PASSWORD && togglePasswordVisibility
            ? {
                  endAdornment: (
                      <InputAdornment position="end">
                          <NgpPasswordVisibility setFieldTypeFunc={setTheFieldType} fieldName={fieldName} />
                      </InputAdornment>
                  ),
              }
            : {};
    const mergedInputProps = assignIn({}, customInputProps || {}, theOtherInputProps);

    const rightAdornment = counterEnabled ? (
        <NgpTextFieldCharCounter control={control} fieldName={fieldName} maxLength={maxLength} />
    ) : null;

    return (
        <NgpField errors={errors} fieldName={fieldName} rightAdornment={rightAdornment}>
            <Controller
                render={({ value, onChange, onBlur }) => {
                    // Fixes the controlled to uncontrolled error as sometimes the api sends null
                    value = isNil(value) ? '' : value;
                    return (
                        <TextField
                            name={fieldName}
                            select={select}
                            multiline={multiline}
                            SelectProps={theSelectProps}
                            label={label}
                            value={value}
                            type={theFieldType}
                            inputRef={inputRef}
                            className={className}
                            error={!isEmpty(get(errors, fieldName))}
                            inputProps={sanitizedInputProps}
                            /* eslint-disable-next-line react/jsx-no-duplicate-props */
                            InputProps={mergedInputProps}
                            InputLabelProps={inputLabelProps}
                            required={required}
                            onClick={onClick}
                            onChange={(e) => {
                                onChange(e);
                                onChangeFunc(e);
                            }}
                            onBlur={(e) => {
                                onBlur(e);
                                onBlurFunc(e);
                            }}
                            disabled={disabled}
                            color={color}
                            fullWidth={fullWidth}
                        >
                            {children}
                        </TextField>
                    );
                }}
                {...defaultInputProps}
                rules={rules}
                control={control}
                name={fieldName}
            />
            {helpTipText && <NgpFieldHelpTip helpText={helpTipText} />}
        </NgpField>
    );
}

NgpTextField.defaultProps = {
    fieldRules: {},
};
